/*
 * Copyright Deskshare Inc.
 * All rights reserved.
 *
 * This software is proprietary and confidential.
 * Unauthorized copying, distribution, or use is strictly prohibited.
 */

import i18n from "i18next";
import { ALL_LANGUAGES } from "@/lib/constants";
import { getStoreBaseUrl } from "@/utils/siteConfig";
import { isShopOrigin } from "@/utils/shopOrigins";

/** The shop's sign-out page, below its language prefix. */
export const SHOP_SIGN_OUT_PATH = "auth/logout";

/**
 * Where the browser goes once this site's sign-out has finished (VM-1, VM-2).
 *
 * The shop keeps its own sign-in in its own address's browser storage, which
 * this site cannot clear, and this site's sign-out never revokes the shop's
 * refresh token. So after a website sign-out the shop stayed signed in, and
 * renewed itself, for up to 30 days: on a shared computer the next person to
 * open the cart saw the previous customer's account.
 *
 * When the shop's sign-out page is live on this environment
 * (VITE_SHOP_SIGNED_OUT_PAGE=true), the browser passes through it: the shop
 * ends its own session only - it never comes back through this site's
 * sign-out - and sends the customer on to `destination`, after checking it is
 * a Deskshare website address. Blank keeps sign-out on this site only, as
 * before, so the order in which the two products are released cannot strand
 * anyone on a page that does not exist.
 *
 * The page is the shop's /{lang}/auth/logout, the address the shop's own
 * work list names for it. The shop owns its routes, so this site follows it.
 *
 * A destination on the shop itself goes straight there: the shop started that
 * sign-out and has already ended its own session. Passing through the shop
 * again would only add a hop.
 *
 * What must still go straight to `destination`, whatever else changes here:
 *   - every destination while the build value is anything but "true";
 *   - a destination on a shop origin (shop.deskshare.com, <label>.shop...);
 *   - a destination that cannot be parsed.
 */
export function afterSignOutUrl(destination: string): string {
  if (import.meta.env.VITE_SHOP_SIGNED_OUT_PAGE !== "true") return destination;
  let target: URL;
  try {
    target = new URL(destination, window.location.origin);
  } catch {
    return destination;
  }
  if (isShopOrigin(target.origin)) return target.href;
  const shown = (i18n.resolvedLanguage ?? i18n.language ?? "en")
    .slice(0, 2)
    .toLowerCase();
  const lang = (ALL_LANGUAGES as readonly string[]).includes(shown)
    ? shown
    : "en";
  const shop = getStoreBaseUrl().replace(/\/+$/, "");
  return `${shop}/${lang}/${SHOP_SIGN_OUT_PATH}?redirect=${encodeURIComponent(target.href)}`;
}
